// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * Pure derivation of the side "Reference" block's view-model from a
 * cycle snapshot. The fiche — stats, models, cost/run, view-prompt
 * link — renders from this single selector (binding mockup 1b). Every
 * figure derives from the persisted `reference_set` record; nothing
 * recomputes from live agents. N/max/min/median come from the shared
 * `deriveReferenceStats` the overlay also uses (one derivation source).
 *
 * `undefined` / empty reference set (feature off / pre-feature snapshot)
 * → `null`, so the block does not render and the side panel lays out
 * exactly as today.
 */

import { deriveReferenceStats, formatCostPerRun } from "./derive"

/** The Reference block's fiche, fully derived from the snapshot. */
export interface ReferenceBlockModel {
  /** N — the disclosed reference-agent count (INV-3). */
  n: number
  /** `true` at N = 1 — hero label swaps, median/min are suppressed. */
  isSingle: boolean
  /** The reference level: `max(reference_set.scores)` — the hero score. */
  max: number
  /** `.toFixed(2)` render of `max` — the hero label. */
  maxLabel: string
  /** Median of the reference scores; `null` at N = 1 (no median). */
  median: number | null
  /** `.toFixed(2)` render of `median`; `null` at N = 1 (no median). */
  medianLabel: string | null
  /** Distribution floor; `null` at N = 1 (no min). */
  min: number | null
  /** `.toFixed(2)` render of `min`; `null` at N = 1 (no min). */
  minLabel: string | null
  /** The references' per-run execution cost — the counterfactual denominator. */
  costPerRun: number
  /** `$`-formatted cost/run, e.g. `$0.0004` (INV-8, no score/€ ratio). */
  costPerRunLabel: string
  /** Model the reference agents run on. */
  executionModel: string
  /** Model that wrote the reference agents. */
  architectModel: string
  /** First persisted reference agent — the "see the agent →" target. */
  agentUuid: string | null
}

interface WithReferenceBlock {
  reference_set?: ReferenceSet
}

const formatScore = (n: number): string => n.toFixed(2)

export const deriveReferenceBlock = (
  dto: WithReferenceBlock,
): ReferenceBlockModel | null => {
  const ref = dto.reference_set
  if (ref === undefined || ref.scores.length === 0) return null

  const { n, max, min, median, hasBand } = deriveReferenceStats(ref.scores)

  return {
    n,
    isSingle: !hasBand,
    max,
    maxLabel: formatScore(max),
    median: hasBand ? median : null,
    medianLabel: hasBand ? formatScore(median) : null,
    min: hasBand ? min : null,
    minLabel: hasBand ? formatScore(min) : null,
    costPerRun: ref.cost_per_run,
    costPerRunLabel: formatCostPerRun(ref.cost_per_run),
    executionModel: ref.execution_model,
    architectModel: ref.architect_model,
    agentUuid: ref.agent_uuids[0] ?? null,
  }
}
